import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { postsAPI } from '../api';
import { Calendar, ArrowRight } from 'lucide-react';

const POSTS_PER_PAGE = 24;

const NewsPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const skip = (page - 1) * POSTS_PER_PAGE;
      const response = await postsAPI.getAll('news', POSTS_PER_PAGE, false, skip);
      setPosts(response.data.filter(p => p.published));
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    postsAPI.getCount('news')
      .then(res => setTotalPosts(res.data.total || 0))
      .catch(err => console.error('Error getting count:', err));
  }, []);

  const totalPages = Math.ceil(totalPosts / POSTS_PER_PAGE);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Paisley News & Breaking Local Updates | Paisley.org.uk"
        description="Latest news from Paisley and Renfrewshire, including local developments, community updates, public services, events and transport."
        keywords="Paisley news, Paisley breaking news, Paisley Scotland news, Renfrewshire news, local Paisley updates"
        url="/news"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Paisley News & Breaking Local Updates",
            description: "Latest news from Paisley and Renfrewshire, including local developments, community updates, public services, events and transport.",
            url: "https://paisley.org.uk/news",
            isPartOf: { "@type": "WebSite", name: "Paisley.org.uk", url: "https://paisley.org.uk" },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://paisley.org.uk" },
              { "@type": "ListItem", position: 2, name: "News", item: "https://paisley.org.uk/news" },
            ],
          },
          ...(posts.length ? [{
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Latest Paisley news",
            numberOfItems: posts.length,
            itemListElement: posts.map((p, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `https://paisley.org.uk/news/${p.slug}`,
              name: p.title,
            })),
          }] : []),
        ]}
      />
      <Header />
      
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4" data-testid="news-page-title">
            Latest Paisley News &amp; <span className="text-cyan-400">Updates</span>
          </h1>
          <p className="text-base lg:text-lg text-gray-300 max-w-2xl mx-auto">
            Stay up to date with the latest news and stories from Paisley
          </p>
          {totalPosts > 0 && (
            <p className="text-sm text-gray-400 mt-3">{totalPosts.toLocaleString()} articles</p>
          )}
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading news...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No news articles available yet.</p>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                {posts.map((post) => (
                  <Link 
                    key={post.id} 
                    to={`/news/${post.slug}`}
                    className="group"
                    data-testid={`news-card-${post.slug}`}
                  >
                    <div className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-cyan-500 hover:shadow-lg transition-all duration-300 h-full">
                      <div className="relative overflow-hidden h-40">
                        <img 
                          src={post.featured_image} 
                          alt={post.title} 
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          onError={(e) => { e.target.src = '/api/files/paisley-org/wp-content/uploads/2025/09/Paisley-skyline-from-abbey-770x513-1.jpg'; }}
                        />
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                          <Calendar size={12} />
                          <span>{new Date(post.created_at).toLocaleDateString()}</span>
                        </div>
                        <h3 className="text-sm font-bold text-slate-900 mb-2 group-hover:text-cyan-500 transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="text-gray-600 text-xs mb-3 line-clamp-2">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center text-cyan-600 text-xs font-medium group-hover:text-cyan-700">
                          Read More
                          <ArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" size={12} />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10" data-testid="news-pagination">
                  <button
                    onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo(0, 0); }}
                    disabled={page === 1}
                    className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-cyan-50 hover:text-cyan-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 text-sm text-gray-600">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo(0, 0); }}
                    disabled={page === totalPages}
                    className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-cyan-50 hover:text-cyan-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NewsPage;
